/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * The constants for global component app
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { icons } from '@assets/index';

import { EModeSSO, IAppReducerState } from './appTypes';

export const GLOBAL_INIT_ERROR_TEXT =
    'Sorry, App can not start. ' +
    'These may be problems with translating or loading the source information. See <App /> component';
export const GLOBAL_INIT_ERROR_TITLE = 'Initialization error!';

/*
 * -------------------------------------------
 *                INIT APP
 * -------------------------------------------
 */

export const INIT_APP_START = '@@b2b_portal_ui/app/INIT_APP_START';
export const INIT_APP_SUCCESS = '@@b2b_portal_ui/app/INIT_APP_SUCCESS';
export const INIT_APP_FAIL = '@@b2b_portal_ui/app/INIT_APP_FAIL';

/*
 * -------------------------------------------
 *                LOAD APP
 * -------------------------------------------
 */

export const LOAD_APP_CONFIG_SUCCESS = '@@b2b_portal_ui/app/LOAD_APP_CONFIG_SUCCESS';
export const LOAD_APP_CONFIG_FAILURE = '@@b2b_portal_ui/app/LOAD_APP_CONFIG_FAILURE';

/*
 * -------------------------------------------
 *                ERROR APP
 * -------------------------------------------
 */

export const SET_APP_ERROR = '@@b2b_portal_ui/app/SET_APP_ERROR';
export const SET_INIT_APP_ERROR = '@@b2b_portal_ui/app/SET_INIT_APP_ERROR';

export const SET_APP_ERROR_CODE = '@@b2b_portal_ui/app/SET_APP_ERROR_CODE';

export const initialAppState: IAppReducerState = {
    code: '',
    config: {
        appInfo: '',
        features: [],
        gtmContainerId: '',
        gtmEnabled: false,
        login: {
            prefix: '',
            showSlider: true,
        },
        sso: {
            changePassword: '',
            logout: '',
            mode: EModeSSO.b2b,
            login: '',
            redirect: '',
        },
    },
    errorMessage: '',
    hasError: false,
    hasInitError: false,
    isLoaded: false,
    isLoading: false,
};

export enum KeyboardKeyCodeEnum {
    ENTER = 13,
    ESCAPE = 27,
}

export enum KeyboardKeyEnum {
    ENTER = 'Enter',
    ESCAPE = 'Escape',
    TAB = 'Tab',
}

export enum EBasketType {
    ACCOUNT = 'ACCOUNT',
    BILLS = 'BILLS',
    BILLS_ACCOUNT = 'BILLS_ACCOUNT',
    BILLS_UNPAID = 'BILLS_UNPAID',
    BILLS_UNPAID_ACCOUNT = 'BILLS_UNPAID_ACCOUNT',
    FIXED = 'FIXED',
    EXPENSES = 'EXPENSES',
    EXPENSES_ACCOUNTS = 'EXPENSES_ACCOUNTS',
    EXPENSES_SUBSCRIBERS = 'EXPENSES_SUBSCRIBERS',
    CAMPAIGN_PARTICIPANTS = 'CAMPAIGN_PARTICIPANTS',
    HEADER = 'HEADER',
    MOBILE = 'MOBILE',
    INQUIRIES = 'INQUIRIES',
    ORDER = 'ORDER',
    MAIN_OPERATIONS = 'MAIN_OPERATIONS',
}

export enum EOperation {
    BILL_INVOICE = 'BILL_INVOICE',
    REPORT_DOCUMENTS = 'REPORT_DOCUMENTS',
}

export enum HttpStatus {
    CONTINUE = 100,
    SWITCHING_PROTOCOLS = 101,
    PROCESSING = 102,
    OK = 200,
    CREATED = 201,
    ACCEPTED = 202,
    NON_AUTHORITATIVE_INFORMATION = 203,
    NO_CONTENT = 204,
    RESET_CONTENT = 205,
    PARTIAL_CONTENT = 206,
    AMBIGUOUS = 300,
    MOVED_PERMANENTLY = 301,
    FOUND = 302,
    SEE_OTHER = 303,
    NOT_MODIFIED = 304,
    TEMPORARY_REDIRECT = 307,
    PERMANENT_REDIRECT = 308,
    BAD_REQUEST = 400,
    UNAUTHORIZED = 401,
    PAYMENT_REQUIRED = 402,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    METHOD_NOT_ALLOWED = 405,
    NOT_ACCEPTABLE = 406,
    PROXY_AUTHENTICATION_REQUIRED = 407,
    REQUEST_TIMEOUT = 408,
    CONFLICT = 409,
    GONE = 410,
    LENGTH_REQUIRED = 411,
    PRECONDITION_FAILED = 412,
    PAYLOAD_TOO_LARGE = 413,
    URI_TOO_LONG = 414,
    UNSUPPORTED_MEDIA_TYPE = 415,
    REQUESTED_RANGE_NOT_SATISFIABLE = 416,
    EXPECTATION_FAILED = 417,
    I_AM_A_TEAPOT = 418,
    UNPROCESSABLE_ENTITY = 422,
    TOO_MANY_REQUESTS = 429,
    INTERNAL_SERVER_ERROR = 500,
    NOT_IMPLEMENTED = 501,
    BAD_GATEWAY = 502,
    SERVICE_UNAVAILABLE = 503,
    GATEWAY_TIMEOUT = 504,
    HTTP_VERSION_NOT_SUPPORTED = 505,
}

export enum EHttpMethod {
    CONNECT = 'CONNECT',
    DELETE = 'DELETE',
    GET = 'GET',
    HEAD = 'HEAD',
    OPTIONS = 'OPTIONS',
    PATCH = 'PATCH',
    POST = 'POST',
    PUT = 'PUT',
    TRACE = 'TRACE',
}

export enum EUrlType {
    EXTERNAL = 'external',
    INTERNAL = 'internal',
}

export enum EMimeType {
    CSV = 'text/csv',
    XLS = 'application/vnd.ms-excel',
    XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export enum EFileExtension {
    CSV = 'csv',
    JPEG = 'jpeg',
    JPG = 'jpg',
    PDF = 'pdf',
    XLS = 'xls',
    XLSX = 'xlsx',
    RTF = 'rtf',
}

export const MapExtensionToIcon: { [key in EFileExtension]: string } = {
    [EFileExtension.CSV]: icons.csvIcon,
    [EFileExtension.JPEG]: icons.jpgIcon,
    [EFileExtension.JPG]: icons.jpgIcon,
    [EFileExtension.PDF]: icons.pdfIcon,
    [EFileExtension.XLS]: icons.fileXlsIcon,
    [EFileExtension.XLSX]: icons.fileXlsxIcon,
    [EFileExtension.RTF]: icons.rtfIcon,
};

export enum EColorStatus {
    ERROR = 'error',
    SUCCESS = 'success',
    SUCCESS_BORDERED = 'success-bordered',
    WARNING = 'warning',
    INFO = 'info',
}

export const ReducerPath = {
    EMD_DELIVERY: 'edmDelivery',
} as const;

export const DEBOUNCE_DEFAULT_DELAY = 500;
